import React, { useRef, useState } from "react";
import { shownWeight, shownWeightUnit, showWeight } from "../../../utils/units";
import { benchmarkAge, clock, liftSet, repPercent, MAX_ESTIMATE_REPS } from "../../../utils/benchmarkNormalise";
import { durationLabel, durationTicks, niceDomain, niceTimeDomain } from "../../../utils/chartScale";

/**
 * Hand-drawn SVG charts for Your curves (issue #91): load against reps for a
 * lift, pace or calories a minute against duration for a machine, and where
 * a lift's 1RM comes from over time. Colours come from the theme: the red
 * primary is the fitted curve, your results are light dots, the rest stays
 * muted.
 */

const W = 440;
const M = { l: 44, r: 12, t: 26, b: 28 };
const PW = W - M.l - M.r;

const round25 = (kg) => Math.round(kg / 2.5) * 2.5;

/** The svg and its tooltip. `tip` is { x, y, title, lines } in chart units. */
const Frame = ({ height, label, tip, svgRef, children }) => (
  <div className="relative">
    <svg
      ref={svgRef}
      viewBox={`0 0 ${W} ${height}`}
      role="img"
      aria-label={label}
      className="block h-auto w-full touch-pan-y select-none overflow-visible"
    >
      {children}
    </svg>
    {tip && (
      <div
        className="pointer-events-none absolute z-10 min-w-[8.5rem] whitespace-nowrap rounded-lg border border-border bg-popover px-2.5 py-1.5 text-xs leading-snug shadow-lg"
        style={{
          left: `${Math.min(80, Math.max(20, (tip.x / W) * 100))}%`,
          top: `${(tip.y / height) * 100}%`,
          transform: "translate(-50%, calc(-100% - 10px))",
        }}
      >
        <p className="font-semibold text-foreground">{tip.title}</p>
        {tip.lines.map((line) => (
          <p key={line} className="text-muted-foreground">
            {line}
          </p>
        ))}
      </div>
    )}
  </div>
);

const Text = ({ children, className = "fill-muted-foreground", ...props }) => (
  <text fontSize="10.5" className={className} style={{ fontVariantNumeric: "tabular-nums" }} {...props}>
    {children}
  </text>
);

const viewX = (event, svg) => {
  const r = svg.getBoundingClientRect();
  return ((event.clientX - r.left) * W) / r.width;
};

const YAxis = ({ ticks, y, unit, format = String }) => (
  <>
    {ticks.map((v) => (
      <g key={v}>
        <line x1={M.l} x2={W - M.r} y1={y(v)} y2={y(v)} className="stroke-border" strokeWidth="1" />
        <Text x={M.l - 6} y={y(v) + 3.5} textAnchor="end">
          {format(v)}
        </Text>
      </g>
    ))}
    <Text x={M.l - 6} y={M.t - 10} textAnchor="end">
      {unit}
    </Text>
  </>
);

const XAxis = ({ ticks, x, height, format = String, caption }) => (
  <>
    <line x1={M.l} x2={W - M.r} y1={height - M.b} y2={height - M.b} className="stroke-muted-foreground/60" />
    {ticks.map((v) => (
      <g key={v}>
        <line x1={x(v)} x2={x(v)} y1={height - M.b} y2={height - M.b + 4} className="stroke-muted-foreground/60" />
        <Text x={x(v)} y={height - M.b + 16} textAnchor="middle">
          {format(v)}
        </Text>
      </g>
    ))}
    {caption && (
      <Text x={W - M.r} y={M.t - 10} textAnchor="end">
        {caption}
      </Text>
    )}
  </>
);

/** A result: filled when it counts fully, hollow when rough, grey when not used. */
const Dot = ({ cx, cy, r = 5.5, state, onEnter, onLeave }) => (
  <g>
    <circle
      cx={cx}
      cy={cy}
      r={r}
      strokeWidth="2"
      className={
        state === "off"
          ? "fill-card stroke-muted-foreground"
          : state === "hollow"
            ? "fill-card stroke-foreground/80"
            : "fill-foreground/80 stroke-foreground/80"
      }
      pointerEvents="none"
    />
    <circle cx={cx} cy={cy} r={Math.max(14, r + 4)} fill="transparent" onPointerEnter={onEnter} onPointerLeave={onLeave} />
  </g>
);

const path = (n, fx, fy) => {
  let d = "";
  for (let i = 0; i <= n; i += 1) d += `${i ? "L" : "M"}${fx(i).toFixed(1)},${fy(i).toFixed(1)}`;
  return d;
};

// ─── Lift: load against reps ─────────────────────────────

const REP_TICKS = [1, 3, 5, 8, 10, 12, 15, 20];

export const LiftCurveChart = ({ curve, bar }) => {
  const svgRef = useRef(null);
  const [tip, setTip] = useState(null);
  const [hover, setHover] = useState(null);
  const H = 290;
  const PH = H - M.t - M.b;

  const points = curve.entries
    .map((e) => ({ ...e, set: liftSet(e.cap) }))
    .filter((e) => e.set && e.set.repsToFailure <= MAX_ESTIMATE_REPS);
  const at = bar != null ? curve.repsAt(bar) : null;
  const values = [curve.kgAt(MAX_ESTIMATE_REPS), curve.oneRm, ...points.map((p) => p.set.load)];
  if (at?.reps) values.push(bar);
  const dom = niceDomain(Math.min(...values) * 0.97, Math.max(...values) * 1.02);
  const x = (r) => M.l + ((r - 1) / (MAX_ESTIMATE_REPS - 1)) * PW;
  const y = (kg) => M.t + (1 - (kg - dom.lo) / (dom.hi - dom.lo)) * PH;
  const sample = (i) => 1 + i / 4;
  const steps = (MAX_ESTIMATE_REPS - 1) * 4;
  const showChart = curve.personal && Math.abs(curve.k - 1) > 0.03;
  const youAbove = curve.k < 1;

  const move = (event) => {
    const r = Math.round(1 + ((viewX(event, svgRef.current) - M.l) / PW) * (MAX_ESTIMATE_REPS - 1));
    const reps = Math.min(MAX_ESTIMATE_REPS, Math.max(1, r));
    const kg = curve.kgAt(reps);
    setHover(reps);
    setTip({
      x: x(reps),
      y: y(kg),
      title: `${reps} rep${reps === 1 ? "" : "s"}: ${"≈"} ${showWeight(round25(kg))}`,
      lines:
        reps + 2 <= MAX_ESTIMATE_REPS
          ? [`to failure. With 2 in reserve, ${"≈"} ${showWeight(round25(curve.kgAt(reps + 2)))}`]
          : ["to failure"],
    });
  };
  const leave = () => {
    setHover(null);
    setTip(null);
  };

  return (
    <Frame
      height={H}
      svgRef={svgRef}
      tip={tip}
      label={`Weight you could lift for 1 to ${MAX_ESTIMATE_REPS} reps, from a 1RM of ${showWeight(curve.oneRm)}`}
    >
      <YAxis ticks={dom.ticks} y={y} unit={shownWeightUnit()} format={(v) => String(shownWeight(v))} />
      <XAxis ticks={REP_TICKS} x={x} height={H} caption="reps to failure" />
      {showChart && (
        <>
          <path
            d={path(steps, (i) => x(sample(i)), (i) => y(curve.oneRm * repPercent(sample(i))))}
            fill="none"
            className="stroke-muted-foreground"
            strokeWidth="1.5"
            strokeDasharray="4 4"
          />
          <Text
            x={x(16)}
            y={y(curve.oneRm * repPercent(16)) + (youAbove ? 16 : -10)}
            textAnchor="middle"
          >
            standard chart
          </Text>
        </>
      )}
      <path
        d={path(steps, (i) => x(sample(i)), (i) => y(curve.kgAt(sample(i))))}
        fill="none"
        className="stroke-primary"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {showChart && (
        <Text x={x(16)} y={y(curve.kgAt(16)) + (youAbove ? -10 : 16)} textAnchor="middle" className="fill-foreground/80">
          you
        </Text>
      )}
      {at?.reps && (
        <g pointerEvents="none">
          <line x1={M.l} x2={x(at.reps)} y1={y(bar)} y2={y(bar)} className="stroke-muted-foreground" strokeDasharray="2 3" />
          <line x1={x(at.reps)} x2={x(at.reps)} y1={y(bar)} y2={H - M.b} className="stroke-muted-foreground" strokeDasharray="2 3" />
          <circle cx={x(at.reps)} cy={y(bar)} r="5" className="fill-primary stroke-card" strokeWidth="2" />
        </g>
      )}
      <rect x={M.l} y={M.t} width={PW} height={PH} fill="transparent" onPointerMove={move} onPointerLeave={leave} />
      {hover != null && (
        <circle cx={x(hover)} cy={y(curve.kgAt(hover))} r="4" className="fill-primary stroke-card" strokeWidth="2" pointerEvents="none" />
      )}
      {points.map((p) => {
        const off = p.share === 0;
        const rtf = p.set.repsToFailure;
        const note = off
          ? "over six months old: not used"
          : p.tested
            ? "your tested max"
            : p.rough
              ? "rough: too far from failure to shape the curve"
              : curve.personal
                ? "shapes your curve"
                : "estimates your 1RM";
        return (
          <Dot
            key={p.cap.id ?? `${p.cap.label}-${p.cap.value}-${p.cap.target_value}`}
            cx={x(rtf)}
            cy={y(p.set.load)}
            state={off ? "off" : p.rough ? "hollow" : "full"}
            onEnter={() =>
              setTip({
                x: x(rtf),
                y: y(p.set.load),
                title: `${showWeight(p.set.load)} ${"×"} ${p.set.reps}${p.cap.effort ? ` · ${p.cap.effort}` : ""}`,
                lines: [`worth about ${Math.round(rtf * 10) / 10} reps to failure`, note],
              })
            }
            onLeave={leave}
          />
        );
      })}
    </Frame>
  );
};

// ─── Machine: pace or calories against duration ──────────

const BANDS = [
  [0, 10, "ATP-PC"],
  [10, 120, "Glycolytic"],
  [120, Infinity, "Aerobic"],
];

/**
 * `points` are every timed effort ({ cap, sec, m, moderate, stale }); `curve`
 * is the fit, or null with too few efforts. `per` is 500 or 1000 for pace,
 * null for calories.
 */
export const WorkCurveChart = ({ curve, points, per }) => {
  const svgRef = useRef(null);
  const [tip, setTip] = useState(null);
  const [hover, setHover] = useState(null);
  const H = 290;
  const PH = H - M.t - M.b;
  const pace = per != null;

  // Pace is seconds per `per` metres (lower is better); calories are per minute.
  const valueOf = (amount, sec) => (pace ? (sec / amount) * per : (amount / sec) * 60);
  const rateValue = (rate) => (pace ? per / rate : rate * 60);
  const format = (v) => (pace ? clock(v) : `${Math.round(v * 10) / 10}`);
  const unit = pace ? `/${per === 500 ? "500 m" : "km"}` : "cal/min";

  const secs = points.map((p) => p.sec);
  const tLo = Math.max(5, Math.min(...secs) / 1.8);
  const tHi = Math.min(7200, Math.max(...secs) * 1.8);
  const x = (t) => M.l + (Math.log(t / tLo) / Math.log(tHi / tLo)) * PW;

  const values = points.map((p) => valueOf(p.m, p.sec));
  if (curve) values.push(rateValue(curve.cs));
  const dom = niceDomain(Math.min(...values) * 0.97, Math.max(...values) * 1.03);
  // Better is up: faster pace, more calories.
  const frac = (v) => (v - dom.lo) / (dom.hi - dom.lo);
  const y = (v) => M.t + (pace ? frac(v) : 1 - frac(v)) * PH;

  const used = new Set(curve ? curve.used.map((p) => p.cap) : []);
  const roleOf = (p) => {
    if (p.moderate) return "Moderate: left out";
    if (p.sec < 120) return "sprint: under 2 minutes";
    if (p.sec > 1800) return "over 30 minutes: not in the fit";
    if (used.has(p.cap)) return "in the threshold fit";
    if (p.stale) return "over six months old: not used";
    return curve ? "a faster effort at this distance counts" : "add another effort for a curve";
  };

  const leave = () => {
    setHover(null);
    setTip(null);
  };
  const move = (event) => {
    if (!curve) return;
    const px = viewX(event, svgRef.current);
    const t = Math.min(curve.span[1], Math.max(curve.span[0], tLo * (tHi / tLo) ** ((px - M.l) / PW)));
    const v = rateValue(curve.cs + curve.reserve / t);
    const amount = curve.cs * t + curve.reserve;
    setHover({ t, v });
    setTip({
      x: x(t),
      y: y(v),
      title: `${format(v)} ${unit}`,
      lines: [
        `holdable for about ${clock(t)}`,
        pace ? `${"≈"} ${Math.round(amount).toLocaleString("en-AU")} m` : `${"≈"} ${Math.round(amount)} cal`,
      ],
    });
  };

  return (
    <Frame
      height={H}
      svgRef={svgRef}
      tip={tip}
      label={
        curve
          ? `${pace ? "Pace" : "Calories a minute"} against duration. Threshold ${format(rateValue(curve.cs))} ${unit}.`
          : `${pace ? "Pace" : "Calories a minute"} against duration. Not enough efforts for a curve yet.`
      }
    >
      {BANDS.map(([a, b, name], i) => {
        const x0 = x(Math.max(a, tLo));
        const x1 = x(Math.min(b, tHi));
        if (x1 <= x0) return null;
        return (
          <g key={name}>
            <rect x={x0} y={M.t} width={x1 - x0} height={PH} className="fill-foreground" opacity={i % 2 ? 0.06 : 0.03} />
            {x1 - x0 > 46 && (
              <Text x={(x0 + x1) / 2} y={M.t - 10} textAnchor="middle" fontSize="9.5" letterSpacing="0.5">
                {name.toUpperCase()}
              </Text>
            )}
          </g>
        );
      })}
      <YAxis ticks={dom.ticks} y={y} unit={unit} format={format} />
      <XAxis ticks={durationTicks(tLo, tHi)} x={x} height={H} format={durationLabel} />
      {curve && (
        <>
          <line
            x1={M.l}
            x2={W - M.r}
            y1={y(rateValue(curve.cs))}
            y2={y(rateValue(curve.cs))}
            className="stroke-primary"
            strokeWidth="1"
            strokeDasharray="4 4"
            opacity="0.7"
          />
          <Text x={M.l + 6} y={y(rateValue(curve.cs)) - 6} className="fill-foreground/80">
            {`threshold ${format(rateValue(curve.cs))}`}
          </Text>
          <path
            d={path(
              60,
              (i) => x(curve.span[0] * (curve.span[1] / curve.span[0]) ** (i / 60)),
              (i) => {
                const t = curve.span[0] * (curve.span[1] / curve.span[0]) ** (i / 60);
                return y(rateValue(curve.cs + curve.reserve / t));
              },
            )}
            fill="none"
            className="stroke-primary"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <rect
            x={x(curve.span[0])}
            y={M.t}
            width={x(curve.span[1]) - x(curve.span[0])}
            height={PH}
            fill="transparent"
            onPointerMove={move}
            onPointerLeave={leave}
          />
          {hover && (
            <circle cx={x(hover.t)} cy={y(hover.v)} r="4" className="fill-primary stroke-card" strokeWidth="2" pointerEvents="none" />
          )}
        </>
      )}
      {points.map((p) => {
        const role = roleOf(p);
        const counts = used.has(p.cap);
        const state = p.moderate || (p.stale && !counts) ? "off" : counts ? "full" : "hollow";
        const v = valueOf(p.m, p.sec);
        return (
          <Dot
            key={p.cap.id ?? `${p.cap.label}-${p.sec}`}
            cx={x(p.sec)}
            cy={y(v)}
            state={state}
            onEnter={() =>
              setTip({
                x: x(p.sec),
                y: y(v),
                title: `${p.cap.label} · ${format(v)} ${unit}`,
                lines: [`${clock(p.sec)}${p.cap.effort ? ` · ${p.cap.effort}` : ""}`, role],
              })
            }
            onLeave={leave}
          />
        );
      })}
    </Frame>
  );
};

// ─── Lift: where the 1RM comes from ──────────────────────

const DAY = 86400000;

export const LiftHistoryChart = ({ curve, now }) => {
  const svgRef = useRef(null);
  const [tip, setTip] = useState(null);
  const H = 220;
  const PH = H - M.t - M.b;

  const dated = curve.entries
    .map((e) => ({ ...e, date: benchmarkAge(e.cap, now)?.date }))
    .filter((e) => e.date);
  const start = new Date(Math.min(now.getTime() - 365 * DAY, ...dated.map((e) => e.date.getTime())));
  const end = new Date(now.getTime() + 14 * DAY);
  const x = (d) => M.l + ((d.getTime() - start.getTime()) / (end.getTime() - start.getTime())) * PW;
  const dom = niceDomain(Math.min(curve.oneRm, ...dated.map((e) => e.kg)) * 0.97, Math.max(curve.oneRm, ...dated.map((e) => e.kg)) * 1.03, 4);
  const y = (kg) => M.t + (1 - (kg - dom.lo) / (dom.hi - dom.lo)) * PH;
  const staleEdge = x(new Date(now.getTime() - 183 * DAY));

  const months = [];
  const m = new Date(start.getFullYear(), start.getMonth() + 1, 1);
  while (m <= end) {
    months.push(new Date(m));
    m.setMonth(m.getMonth() + 2);
  }

  return (
    <Frame height={H} svgRef={svgRef} tip={tip} label={`Estimated 1RM of each result by date, combined to about ${showWeight(curve.oneRm)}`}>
      {staleEdge > M.l && (
        <>
          <rect x={M.l} y={M.t} width={staleEdge - M.l} height={PH} className="fill-foreground" opacity="0.03" />
          <Text x={(M.l + staleEdge) / 2} y={M.t - 10} textAnchor="middle" fontSize="9.5" letterSpacing="0.5">
            OVER SIX MONTHS OLD
          </Text>
        </>
      )}
      <YAxis ticks={dom.ticks} y={y} unit={shownWeightUnit()} format={(v) => String(shownWeight(v))} />
      <XAxis
        ticks={months.map((d) => d.getTime())}
        x={(t) => x(new Date(t))}
        height={H}
        format={(t) => new Date(t).toLocaleDateString("en-AU", { month: "short" })}
      />
      <line x1={M.l} x2={W - M.r} y1={y(curve.oneRm)} y2={y(curve.oneRm)} className="stroke-primary" strokeWidth="2" />
      <Text x={M.l + 6} y={y(curve.oneRm) - 7} className="fill-foreground/80">
        {`combined ${"≈"} ${showWeight(curve.oneRm)}`}
      </Text>
      {dated.map((e) => {
        const off = e.share === 0;
        const r = off ? 5 : 5 + 7 * Math.sqrt(e.share);
        return (
          <Dot
            key={e.cap.id ?? `${e.cap.label}-${e.date.getTime()}`}
            cx={x(e.date)}
            cy={y(e.kg)}
            r={r}
            state={off ? "off" : e.rough ? "hollow" : "full"}
            onEnter={() =>
              setTip({
                x: x(e.date),
                y: y(e.kg),
                title: `${"≈"} ${showWeight(e.kg)} · ${e.cap.label}`,
                lines: [
                  `${e.tested ? "tested max" : `estimated 1RM${e.rough ? ", rough" : ""}`} · ${e.date.toLocaleDateString("en-AU", { day: "numeric", month: "short" })}`,
                  off ? "not counted: there are newer results" : `counts for ${Math.round(e.share * 100)}%`,
                ],
              })
            }
            onLeave={() => setTip(null)}
          />
        );
      })}
    </Frame>
  );
};

// ─── Anything else: progress over time ───────────────────

// Month ticks every 1, 2, 3, 6 or 12 months, whichever gives at most six.
const monthTicks = (start, end) => {
  const span = (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth();
  const every = [1, 2, 3, 6, 12].find((n) => span / n <= 6) || 12;
  const ticks = [];
  const m = new Date(start.getFullYear(), start.getMonth() + 1, 1);
  if (every === 12) m.setMonth(0, 1);
  while (m < start) m.setMonth(m.getMonth() + every);
  while (m <= end) {
    ticks.push(m.getTime());
    m.setMonth(m.getMonth() + every);
  }
  return { ticks, years: every === 12 || start.getFullYear() !== end.getFullYear() };
};

/**
 * `series` is progressSeries: each result by date, joined in order, with the
 * best as a dashed line. Better is up, so a time for a job done is drawn
 * with faster at the top.
 */
export const ProgressChart = ({ series, format, now }) => {
  const svgRef = useRef(null);
  const [tip, setTip] = useState(null);
  const H = 220;
  const PH = H - M.t - M.b;
  const { points, best, higher } = series;

  const firstT = points[0].date.getTime();
  const lastT = Math.max(now.getTime(), points[points.length - 1].date.getTime());
  const pad = Math.max(14 * DAY, (lastT - firstT) * 0.05);
  const start = new Date(Math.min(firstT - pad, now.getTime() - 90 * DAY));
  const end = new Date(lastT + pad);
  const x = (t) => M.l + ((t - start.getTime()) / (end.getTime() - start.getTime())) * PW;
  const values = points.map((p) => p.value);
  const vLo = Math.min(...values);
  const vHi = Math.max(...values);
  const gap = (vHi - vLo || vHi * 0.1) * 0.2;
  const dom = (series.unit === "sec" ? niceTimeDomain : niceDomain)(Math.max(0, vLo - gap), vHi + gap, 4);
  const frac = (v) => (v - dom.lo) / (dom.hi - dom.lo);
  const y = (v) => M.t + (higher ? 1 - frac(v) : frac(v)) * PH;
  const months = monthTicks(start, end);
  const axisFormat = series.unit === "sec" ? clock : (v) => v.toLocaleString("en-AU");
  const dayLabel = (d) => d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });

  return (
    <Frame
      height={H}
      svgRef={svgRef}
      tip={tip}
      label={`Each result by date, from ${format(points[0].value)} to ${format(series.latest.value)}. Best ${format(best.value)}.`}
    >
      <YAxis ticks={dom.ticks} y={y} unit={series.unit === "sec" ? "" : series.unit} format={axisFormat} />
      <XAxis
        ticks={months.ticks}
        x={x}
        height={H}
        format={(t) =>
          new Date(t).toLocaleDateString("en-AU", months.years ? { month: "short", year: "2-digit" } : { month: "short" })
        }
      />
      <line
        x1={M.l}
        x2={W - M.r}
        y1={y(best.value)}
        y2={y(best.value)}
        className="stroke-muted-foreground"
        strokeWidth="1"
        strokeDasharray="4 4"
      />
      <Text x={M.l + 6} y={y(best.value) + (higher ? -6 : 14)} className="fill-foreground/80">
        {`best ${format(best.value)}`}
      </Text>
      {points.length > 1 && (
        <path
          d={path(points.length - 1, (i) => x(points[i].date.getTime()), (i) => y(points[i].value))}
          fill="none"
          className="stroke-primary"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      {points.map((p) => (
        <Dot
          key={p.cap.id ?? `${p.cap.label}-${p.date.getTime()}-${p.value}`}
          cx={x(p.date.getTime())}
          cy={y(p.value)}
          state={p.moderate ? "hollow" : "full"}
          onEnter={() =>
            setTip({
              x: x(p.date.getTime()),
              y: y(p.value),
              title: `${format(p.value)} · ${p.cap.label}`,
              lines: [`${dayLabel(p.date)}${p.cap.effort ? ` · ${p.cap.effort}` : ""}`].concat(
                p === best ? ["your best"] : [],
              ),
            })
          }
          onLeave={() => setTip(null)}
        />
      ))}
    </Frame>
  );
};
